import React, { useState, useEffect } from 'react';
import authenticate from '../../utils/authenticate';
import './ExtraSelectionStep.css';
import { ApiResponse, Extra, Modality, Step2Response, Turn } from '../../models/types';
import { get } from '../../services/api';
import ExtraItem, { getOptionalExtraCardLabels } from '../ExtraItem/ExtraItem';
import { GiftSectionIcon } from '../ExtraItem/ExtraIcons';
import ExtraInfoModal from '../ExtraInfoModal/ExtraInfoModal';
import { config } from "../../config/environment";
import { getDefaultExtraAmount, sortExtrasByCatalogOrder } from '../../utils/extraPricing';
import {
    getVillalbaStandaloneMonitorLine,
    formatIncludedExtraSelectionUnits,
    formatMonitorBonusUnitsLabel,
} from '../../utils/villalbaPromotions';
import {
    filterOutMeriendaPerritoExtra,
    findMeriendaPerritoUpgrade,
    formatMeriendaTypeLabel,
    formatMeriendaUpgradePriceLabel,
    getSelectedMeriendaType,
    hasMeriendaTypeChoice,
    isMeriendaExtra,
    MeriendaType,
} from '../../utils/meriendaUpgrade';
import { formatExtraDisplayName } from '../../utils/extraDisplayName';

interface ExtraSelectionStepProps {
    shopId: number;
    shopName: string;
    roomId: number;
    selectedDate: Date | null;
    selectedTurn: Turn | null;
    selectedModality: Modality | null;
    selectedExtras: Extra[];
    onExtrasSelect: (extras: Extra[]) => void;
    onAvailableExtrasChange: (extras: Extra[]) => void;
}

const currencyFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const ExtraSelectionStep: React.FC<ExtraSelectionStepProps> = ({
    shopId,
    shopName,
    roomId,
    selectedDate,
    selectedTurn,
    selectedModality,
    selectedExtras,
    onExtrasSelect,
    onAvailableExtrasChange,
}) => {
    const [includedExtras, setIncludedExtras] = useState<Step2Response['included_extras']>([]);
    const [extras, setExtras] = useState<Extra[]>([]);
    const [selectedExtra, setSelectedExtra] = useState<Extra | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const fetchExtras = async () => {
            if (!selectedDate || !selectedTurn || !selectedModality) return;

            const token = await authenticate();
            if (!token) {
                alert('Failed to authenticate');
                return;
            }

            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');
            const endHour = selectedTurn.time_end.split(':')[0].padStart(2, '0');

            const url = `${config.API_BASE_URL}/clientapi/buy/step2/shop/${shopId}/room/${roomId}/turn/${selectedTurn.turn_id}/modality/${selectedModality.modalityId}/${year}/${month}/${day}/${endHour}`;
            await get(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            .then((response: ApiResponse) => {
                if (response.error) {
                    console.error('Error fetching extras:', response.message);
                    setIncludedExtras([]);
                    setExtras([]);
                    return;
                }

                const data = response.message as Step2Response;
                setIncludedExtras(data.included_extras ?? []);
                setExtras(data.extras ?? []);
                onAvailableExtrasChange(data.extras ?? []);
            })
            .catch(error => {
                console.error('Error fetching available extras:', error);
                setIncludedExtras([]);
                setExtras([]);
                onAvailableExtrasChange([]);
            });
        };

        fetchExtras();
    }, [shopId, roomId, selectedDate, selectedTurn, selectedModality, onAvailableExtrasChange]);

    useEffect(() => {
        if (selectedExtras.length === 0) {
            return;
        }

        // Until step2 has loaded for this modality, keep current selections.
        if (extras.length === 0 && includedExtras.length === 0) {
            return;
        }

        const availableIds = new Set(extras.map((extra) => Number(extra.extra_id)));
        const includedIds = new Set(
            includedExtras.map((extra) => Number(extra.extra_id))
        );

        const prunedExtras = selectedExtras.filter((extra) => {
            const extraId = Number(extra.extra_id);
            if (includedIds.has(extraId)) {
                return false;
            }
            // Drop extras no longer offered as optional for this modality
            // (e.g. Monitor/Limpieza after switching Flexible → Asistida).
            return availableIds.has(extraId);
        });

        const sortedExtras = sortExtrasByCatalogOrder(prunedExtras, extras);
        const hasSameSelection =
            sortedExtras.length === selectedExtras.length &&
            sortedExtras.every(
                (extra, index) => extra.extra_id === selectedExtras[index]?.extra_id
            );

        if (!hasSameSelection) {
            onExtrasSelect(sortedExtras);
        }
    }, [extras, includedExtras, selectedExtras, onExtrasSelect]);

    const updateSelectedExtras = (nextExtras: Extra[]) => {
        onExtrasSelect(sortExtrasByCatalogOrder(nextExtras, extras));
    };

    const openAmountModal = (extra: Extra) => {
        setSelectedExtra(extra);
        setIsModalOpen(true);
    };

    const handleAddExtra = (extra: Extra) => {
        const isAlreadySelected = selectedExtras.some((e) => e.extra_id === extra.extra_id);

        if (extra.info_type === 'dialog_int_amount') {
            openAmountModal(extra);
            return;
        }

        if (isAlreadySelected) {
            return;
        }

        updateSelectedExtras([
            ...selectedExtras,
            { ...extra, extra_amount: getDefaultExtraAmount(extra) },
        ]);
    };

    const handleRemoveExtra = (extra: Extra) => {
        updateSelectedExtras(selectedExtras.filter((e) => e.extra_id !== extra.extra_id));
    };

    const handleModalConfirm = (updatedExtra: Extra) => {
        if (!selectedExtras.some(e => e.extra_id === updatedExtra.extra_id)) {
            updateSelectedExtras([...selectedExtras, updatedExtra]);
        } else {
            const updatedExtras = selectedExtras.map(extra =>
                extra.extra_id === updatedExtra.extra_id ? updatedExtra : extra
            );
            updateSelectedExtras(updatedExtras);
        }
    };

    const handleModalRemove = (extra: Extra) => {
        updateSelectedExtras(selectedExtras.filter(e => e.extra_id !== extra.extra_id));
    };

    const meriendaPerritoUpgrade = findMeriendaPerritoUpgrade(extras);
    const showMeriendaTypeChoice = hasMeriendaTypeChoice(includedExtras, extras);
    const selectedMeriendaType = getSelectedMeriendaType(selectedExtras);
    const optionalExtras = showMeriendaTypeChoice
        ? filterOutMeriendaPerritoExtra(extras)
        : extras;

    const handleMeriendaTypeSelect = (type: MeriendaType) => {
        if (!meriendaPerritoUpgrade) {
            return;
        }

        const withoutPerrito = filterOutMeriendaPerritoExtra(selectedExtras);

        if (type === 'sandwich') {
            updateSelectedExtras(withoutPerrito);
            return;
        }

        updateSelectedExtras([
            ...withoutPerrito,
            {
                ...meriendaPerritoUpgrade,
                extra_amount: getDefaultExtraAmount(meriendaPerritoUpgrade),
            },
        ]);
    };

    const standaloneMonitorLine = getVillalbaStandaloneMonitorLine(
        shopId,
        selectedExtras,
        includedExtras,
        shopName
    );
    const standaloneMonitorUnits = standaloneMonitorLine
        ? formatMonitorBonusUnitsLabel(standaloneMonitorLine)
        : '';
    const hasIncludedExtras = includedExtras.length > 0 || standaloneMonitorLine !== null;
    const includedSectionTitle = includedExtras.length > 0
        ? 'Incluidos en tu modalidad'
        : 'Incluidos con tu selección';
    const meriendaUpgradePriceLabel = meriendaPerritoUpgrade
        ? formatMeriendaUpgradePriceLabel(meriendaPerritoUpgrade, currencyFormatter)
        : '';

    return (
        <div className="extras-step">
            {hasIncludedExtras && (
                <section className="extras-section extras-section--included">
                    <div className="extras-section-header">
                        <div className="extras-section-heading">
                            <span className="extras-section-icon extras-section-icon--included" aria-hidden="true">
                                <GiftSectionIcon />
                            </span>
                            <div className="extras-section-copy">
                                <h3 className="extras-section-title">{includedSectionTitle}</h3>
                                <p className="extras-section-subtitle">
                                    Estos extras ya forman parte de tu reserva.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="extras-grid">
                        {includedExtras.map((extra) => {
                            const unitsLabel = formatIncludedExtraSelectionUnits(
                                extra,
                                shopId,
                                selectedExtras,
                                shopName
                            );
                            const isMeriendaWithChoice =
                                showMeriendaTypeChoice && isMeriendaExtra(extra);

                            return (
                                <ExtraItem
                                    key={extra.extra_id}
                                    name={extra.extra_name}
                                    unitsLabel={unitsLabel || undefined}
                                    variant="included"
                                >
                                    {isMeriendaWithChoice && (
                                        <div
                                            className="merienda-type-options"
                                            role="radiogroup"
                                            aria-label="Tipo de merienda"
                                        >
                                            <button
                                                type="button"
                                                role="radio"
                                                aria-checked={selectedMeriendaType === 'sandwich'}
                                                className={`merienda-type-option${selectedMeriendaType === 'sandwich' ? ' selected' : ''}`}
                                                onClick={() => handleMeriendaTypeSelect('sandwich')}
                                            >
                                                <span className="merienda-type-option-name">
                                                    {formatMeriendaTypeLabel('sandwich')}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                role="radio"
                                                aria-checked={selectedMeriendaType === 'perrito'}
                                                className={`merienda-type-option${selectedMeriendaType === 'perrito' ? ' selected' : ''}`}
                                                onClick={() => handleMeriendaTypeSelect('perrito')}
                                            >
                                                <span className="merienda-type-option-name">
                                                    {formatMeriendaTypeLabel('perrito')}
                                                </span>
                                                {meriendaUpgradePriceLabel ? (
                                                    <span className="merienda-type-option-price">
                                                        {meriendaUpgradePriceLabel}
                                                    </span>
                                                ) : null}
                                            </button>
                                        </div>
                                    )}
                                </ExtraItem>
                            );
                        })}
                        {standaloneMonitorLine && (
                            <ExtraItem
                                name={standaloneMonitorLine.extra_name}
                                unitsLabel={standaloneMonitorUnits || undefined}
                                variant="included"
                            />
                        )}
                    </div>
                </section>
            )}

            {optionalExtras.length > 0 && (
                <section className="extras-section extras-section--optional">
                    <div className="extras-section-header">
                        <div className="extras-section-heading">
                            <span className="extras-section-icon" aria-hidden="true">
                                <GiftSectionIcon />
                            </span>
                            <div className="extras-section-copy">
                                <h3 className="extras-section-title">Extras opcionales</h3>
                                <p className="extras-section-subtitle">
                                    Pulsa{' '}
                                    <span className="extras-hint-symbol extras-hint-symbol--add" aria-hidden="true">
                                        +
                                    </span>{' '}
                                    para añadir y{' '}
                                    <span className="extras-hint-symbol extras-hint-symbol--check" aria-hidden="true">
                                        ✓
                                    </span>{' '}
                                    para quitar.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="extras-grid">
                        {optionalExtras.map((extra) => {
                            const selectedExtraItem = selectedExtras.find(
                                (item) => item.extra_id === extra.extra_id
                            );
                            const isSelected = Boolean(selectedExtraItem);
                            const displayExtra = selectedExtraItem ?? extra;
                            const { priceLabel, unitsLabel, canAdjustAmount } =
                                getOptionalExtraCardLabels(displayExtra, isSelected);

                            return (
                                <ExtraItem
                                    key={extra.extra_id}
                                    name={formatExtraDisplayName(extra)}
                                    priceLabel={priceLabel}
                                    unitsLabel={unitsLabel || undefined}
                                    selected={isSelected}
                                    canAdjustAmount={canAdjustAmount}
                                    onAdd={() => handleAddExtra(extra)}
                                    onRemove={() => handleRemoveExtra(extra)}
                                />
                            );
                        })}
                    </div>
                </section>
            )}

            {selectedExtra && (
                <ExtraInfoModal
                    key={selectedExtra.extra_id}
                    extra={selectedExtras.find(e => e.extra_id === selectedExtra.extra_id) ?? selectedExtra}
                    selected={selectedExtras.some(e => e.extra_id === selectedExtra.extra_id)}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onConfirm={handleModalConfirm}
                    onRemove={handleModalRemove}
                />
            )}

        </div>
    );
};

export default ExtraSelectionStep;
